import React from 'react';

import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import Modal, { ModalSize } from 'components/modal';
import Picture from 'components/picture';
import SanityBlockRenderer, {
  IBlockContentOverrides,
  ISerializers,
  PassthroughComponent,
} from 'components/sanity-block-renderer';
import { ISystemwideOffer } from 'generated/sanity-graphql';
import useMediaQuery from 'hooks/use-media-query';
import { LoyaltyOffer } from 'state/loyalty/types';

import {
  ButtonWrapper,
  Description,
  MobileButton,
  TextContent,
  Title,
  UpgradeContainer,
  UpsellModalContent,
  UpsellModalHeading,
  UpsellOption,
  UpsellOptionsWrapper,
} from './offer-upsell-modal.styled';

const titleBlockContentOverrides: IBlockContentOverrides = {
  normal: Title,
};

const titleBlockSerializers: ISerializers = {
  marks: {
    em: PassthroughComponent,
  },
  container: PassthroughComponent,
};

const descriptionBlockContentOverrides: IBlockContentOverrides = {
  normal: Description,
};

const descriptionBlockSerializers: ISerializers = {
  container: PassthroughComponent,
};

interface IOfferUpsellModal {
  initialOfferId: string;
  upsellOptions: LoyaltyOffer[];
  onSelect: (offer: ISystemwideOffer) => void;
  onDismiss: () => void;
}

const OfferUpsellModal: React.FC<IOfferUpsellModal> = ({
  onSelect,
  onDismiss,
  initialOfferId,
  upsellOptions,
}) => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery('mobile');

  return (
    <Modal
      data-testid="loyalty-offer-upsell-modal"
      onDismiss={onDismiss}
      size={upsellOptions?.length > 1 ? ModalSize.REGULAR : ModalSize.SMALL}
      height={'40rem'}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Upsell Offer',
        modalMessage: `Parent Offer ID: ${initialOfferId}`,
      }}
    >
      <UpsellModalContent>
        <UpsellModalHeading id="modal-heading">
          {formatMessage({ id: 'offerUpsellTitle' })}
        </UpsellModalHeading>
        <UpsellOptionsWrapper $numColumns={upsellOptions.length > 1 ? 2 : 1}>
          {upsellOptions.map(offer => (
            <>
              <UpsellOption key={offer?._id} $limitWidth={upsellOptions.length > 1}>
                <Picture
                  image={offer?.localizedImage?.locale?.app}
                  alt=""
                  objectFitContain={true}
                />
                <TextContent>
                  <SanityBlockRenderer
                    blockContentOverrides={titleBlockContentOverrides}
                    serializers={titleBlockSerializers}
                    content={offer?.name?.localeRaw}
                  />
                  <SanityBlockRenderer
                    blockContentOverrides={descriptionBlockContentOverrides}
                    serializers={descriptionBlockSerializers}
                    content={offer?.description?.localeRaw}
                  />
                </TextContent>
              </UpsellOption>
              {isMobile && (
                <MobileButton
                  key={offer?._id}
                  variant={ActionButtonVariants.PRIMARY}
                  data-testid={`${offer?._id}__offer-upsell`}
                  onClick={() => onSelect(offer as ISystemwideOffer)}
                >
                  {formatMessage({ id: 'offerUpsellUpgrade' })}
                </MobileButton>
              )}
            </>
          ))}
        </UpsellOptionsWrapper>
        <ButtonWrapper>
          <UpgradeContainer $center={upsellOptions.length < 2}>
            {upsellOptions.map(offer => (
              <ActionButton
                key={offer?._id}
                variant={ActionButtonVariants.PRIMARY}
                data-testid={`${offer?._id}__offer-upsell`}
                onClick={() => onSelect(offer as ISystemwideOffer)}
              >
                {formatMessage({ id: 'offerUpsellUpgrade' })}
              </ActionButton>
            ))}
          </UpgradeContainer>
          <ActionButton
            variant={ActionButtonVariants.OUTLINE}
            data-testid="offer-upsell-discard"
            onClick={onDismiss}
          >
            {formatMessage({ id: 'offerUpsellDiscard' })}
          </ActionButton>
        </ButtonWrapper>
      </UpsellModalContent>
    </Modal>
  );
};

export default OfferUpsellModal;
